import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Alert,
  Box,
  Button,
  Chip,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Autocomplete,
  TextField,
  Collapse,
} from '@mui/material';

import {
  Menu as MenuIcon,
  Dashboard,
  Business,
  People,
  LocalShipping,
  Recycling,
  Assignment,
  Storage,
  DarkMode,
  LightMode,
  Logout,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  Analytics,
  FactCheck,
  VerifiedUser,
  VpnKey,
  AccountBalanceWallet,
  Sensors,
} from '@mui/icons-material';

import { useAuthStore } from '../stores/auth.store';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationSchema } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import { NAV_CATALOG, isNavGroup, isVerifierOnlyUser } from '../auth';
import ContactSupportButton from './ContactSupportButton';

const drawerWidth = 280;
const appBarHeight = 64;

const MENU_ICONS: Record<string, React.ReactNode> = {
  Dashboard: <Dashboard />,
  Organizations: <Business />,
  Users: <People />,
  'API Keys': <VpnKey />,
  Approvals: <FactCheck />,
  Verification: <VerifiedUser />,
  Finance: <AccountBalanceWallet />,
  Operations: <Analytics />,
  Orders: <Assignment />,
  Transportation: <LocalShipping />,
  Recycling: <Recycling />,
  Containers: <Storage />,
  Devices: <Sensors />,
  ESG: <Analytics />,
};

interface LayoutProps {
  children: React.ReactNode;
  onThemeToggle: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, onThemeToggle }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, accessibleOrgIds, isPlatformAdmin: platformAdmin, permVerStale, tokenPayload } =
    useAuthStore();
  const {
    organizations,
    getOrganizations,
    selectedOrganization,
    setSelectedOrganization,
  } = useOrganizationStore();
  const { isReadOnly, filterMenuEntries } = useAuthPermissions();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  const verifierOnly = isVerifierOnlyUser(tokenPayload);

  const visibleMenuItems = useMemo(
    () => filterMenuEntries(NAV_CATALOG),
    [filterMenuEntries],
  );

  const orgOptions = useMemo(() => {
    if (platformAdmin) {
      return organizations;
    }
    if (accessibleOrgIds.length === 0) {
      return organizations;
    }
    const allowed = new Set(accessibleOrgIds);
    return organizations.filter((o) => allowed.has(o.id));
  }, [organizations, accessibleOrgIds, platformAdmin]);

  useEffect(() => {
    if (!selectedOrganization) {
      const saved = sessionStorage.getItem('selectedOrganization');
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as OrganizationSchema;
          if (
            platformAdmin ||
            accessibleOrgIds.length === 0 ||
            accessibleOrgIds.includes(parsed.id)
          ) {
            setSelectedOrganization(parsed);
          } else {
            sessionStorage.removeItem('selectedOrganization');
          }
        } catch {
          sessionStorage.removeItem('selectedOrganization');
        }
      }
    }
  }, []);

  useEffect(() => {
    if (selectedOrganization || orgOptions.length !== 1) {
      return;
    }
    const only = orgOptions[0];
    setSelectedOrganization(only);
    sessionStorage.setItem('selectedOrganization', JSON.stringify(only));
  }, [orgOptions, selectedOrganization, setSelectedOrganization]);

  useEffect(() => {
    getOrganizations({ page: 1, limit: 10, name: search || undefined });
  }, [search]);

  useEffect(() => {
    const state: Record<string, boolean> = {};
    visibleMenuItems.forEach((m) => {
      if (isNavGroup(m) && m.subItems.some((s) => location.pathname.startsWith(s.path))) {
        state[m.text] = true;
      }
    });
    setOpenMenus((prev) => ({ ...prev, ...state }));
  }, [location.pathname, visibleMenuItems]);

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{ height: appBarHeight }}>
        <AdminPanelSettings color="primary" sx={{ mr: 1 }} />
        <Box>
          <Typography fontWeight={700}>Recyclee Admin</Typography>
          {verifierOnly && (
            <Typography variant="caption" color="text.secondary">
              Verification portal
            </Typography>
          )}
        </Box>
      </Toolbar>

      <Divider />

      {!verifierOnly && (
        <Box px={2} py={2}>
          <Autocomplete
            size="small"
            value={selectedOrganization}
            options={orgOptions}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            getOptionLabel={(o) => o.name}
            onChange={(_, v: OrganizationSchema | null) => {
              if (!v) return;
              setSelectedOrganization(v);
              sessionStorage.setItem('selectedOrganization', JSON.stringify(v));
            }}
            onInputChange={(_, v) => setSearch(v)}
            renderInput={(params) => <TextField {...params} label="Organization" />}
          />
        </Box>
      )}

      {!verifierOnly && <Divider />}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
        <List>
          {visibleMenuItems.map((item) => {
            if (isNavGroup(item)) {
              const active = item.subItems.some((s) =>
                location.pathname.startsWith(s.path),
              );
              return (
                <Box key={item.text}>
                  <ListItemButton
                    selected={active}
                    onClick={() =>
                      setOpenMenus((p) => ({ ...p, [item.text]: !p[item.text] }))
                    }
                    sx={{ borderRadius: 2, mb: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {MENU_ICONS[item.text]}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                    {openMenus[item.text] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={openMenus[item.text]}>
                    <List disablePadding>
                      {item.subItems.map((sub) => (
                        <ListItemButton
                          key={sub.path}
                          sx={{ pl: 6, borderRadius: 2 }}
                          selected={location.pathname === sub.path}
                          onClick={() => navigate(sub.path)}
                        >
                          <ListItemText primary={sub.text} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            }

            const active = item.path === location.pathname;
            return (
              <Box key={item.text}>
                <ListItemButton
                  selected={active}
                  onClick={() => navigate(item.path)}
                  sx={{ borderRadius: 2, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {MENU_ICONS[item.text]}
                  </ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItemButton>
              </Box>
            );
          })}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <CssBaseline />

      <AppBar position="fixed" sx={{ height: appBarHeight }}>
        <Toolbar>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box flexGrow={1} />

          {platformAdmin && (
            <Chip
              label="Platform admin"
              color="primary"
              size="small"
              sx={{ mr: 1, fontWeight: 700 }}
            />
          )}

          {isReadOnly && (
            <Chip
              label="Read-only"
              color="warning"
              size="small"
              sx={{ mr: 1.5, fontWeight: 700 }}
            />
          )}

          <IconButton onClick={onThemeToggle}>
            {theme.palette.mode === 'dark' ? <LightMode /> : <DarkMode />}
          </IconButton>

          <Box
            onClick={(e) => setProfileAnchor(e.currentTarget)}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ml: 2 }}
          >
            <Avatar sx={{ mr: 1 }}>{user?.username?.[0]}</Avatar>
            <Typography variant="body2">{user?.username}</Typography>
          </Box>

          <Menu
            anchorEl={profileAnchor}
            open={!!profileAnchor}
            onClose={() => setProfileAnchor(null)}
          >
            <MenuItem onClick={logout}>
              <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flex: 1, pt: `${appBarHeight}px` }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { sm: 'none' }, width: drawerWidth }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Box
          component="main"
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            bgcolor: theme.palette.background.default,
          }}
        >
          {permVerStale && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => logout()}>
                  Log out
                </Button>
              }
            >
              Your permissions have changed. Please log out and sign in again for updates to take
              effect.
            </Alert>
          )}
          {children}
        </Box>
      </Box>
      {selectedOrganization && (
        <ContactSupportButton organizationId={selectedOrganization.id} />
      )}
    </Box>
  );
};

export default Layout;
